import { Navigate, useLocation, useParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { usePool } from '@/hooks';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * When true, requires the authenticated user to be the host or cohost
   * of the pool identified by `:poolId` in the route. Non-hosts are
   * redirected to the pool details page.
   *
   * Used by routes such as `/pools/:poolId/mulligan-admin` per the
   * Mid-Year Mulligan design (Frontend Components → Routing).
   */
  requiresHost?: boolean;
}

export function ProtectedRoute({ children, requiresHost = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiresHost) {
    return <HostGuard>{children}</HostGuard>;
  }

  return <>{children}</>;
}

/**
 * Verifies that the authenticated user has host (or cohost) permissions
 * for the pool identified by `:poolId` in the URL. Rendered as a child of
 * `ProtectedRoute` so it only mounts after authentication is confirmed —
 * this avoids issuing pool API calls for unauthenticated visitors.
 */
function HostGuard({ children }: { children: React.ReactNode }) {
  const { poolId } = useParams<{ poolId: string }>();
  const { data: poolData, isLoading, error } = usePool(poolId || '');

  if (!poolId) {
    // No pool context — bounce to dashboard rather than render an empty page.
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !poolData?.membership) {
    return <Navigate to={`/pools/${poolId}`} replace />;
  }

  const role = poolData.membership.role;
  const isHost = role === 'host' || role === 'cohost';

  if (!isHost) {
    return <Navigate to={`/pools/${poolId}`} replace />;
  }

  return <>{children}</>;
}
